/**
 * OpenAI Admin API Key storage.
 * Secure storage, retrieval, and deletion of the Admin API Key.
 */
import { STORAGE_VERSION, StoredApiKey, sanitizeApiKey, encode, decode } from './internal';
import { markAdminCredentialChanged } from './admin-credential-revision';

export const OPENAI_ADMIN_API_KEY_STORAGE_KEY = 'openai_admin_api_key';

/**
 * Save Admin API Key to local storage
 */
export const saveApiKey = (apiKey: string): boolean => {
  try {
    if (!apiKey || typeof apiKey !== 'string') {
      throw new Error('Invalid API key');
    }
    const normalizedApiKey = sanitizeApiKey(apiKey);
    if (!normalizedApiKey) {
      throw new Error('Invalid API key');
    }

    const data: StoredApiKey = {
      value: encode(normalizedApiKey),
      createdAt: Date.now(),
      version: STORAGE_VERSION
    };

    localStorage.setItem(OPENAI_ADMIN_API_KEY_STORAGE_KEY, JSON.stringify(data));
    markAdminCredentialChanged();
    return true;
  } catch (error) {
    console.error('Failed to save API key:', error);
    return false;
  }
};

/**
 * Get Admin API Key from local storage or environment variable
 */
export const getApiKey = (): string | null => {
  try {
    // First, check if API key is available from environment variable
    if (import.meta.env.VITE_OPENAI_ADMIN_API_KEY) {
      return import.meta.env.VITE_OPENAI_ADMIN_API_KEY;
    }

    // Fall back to local storage
    const stored = localStorage.getItem(OPENAI_ADMIN_API_KEY_STORAGE_KEY);
    if (!stored) return null;

    const data: StoredApiKey = JSON.parse(stored);

    // Version check
    if (data.version !== STORAGE_VERSION) {
      removeApiKey();
      return null;
    }

    const decoded = decode(data.value);
    if (!decoded) return null;
    return sanitizeApiKey(decoded) || null;
  } catch (error) {
    console.error('Failed to get API key:', error);
    removeApiKey(); // Remove corrupted data
    return null;
  }
};

/**
 * Remove Admin API Key from local storage
 */
export const removeApiKey = (): boolean => {
  try {
    localStorage.removeItem(OPENAI_ADMIN_API_KEY_STORAGE_KEY);
    markAdminCredentialChanged();
    return true;
  } catch (error) {
    console.error('Failed to remove API key:', error);
    return false;
  }
};

/**
 * Check if API Key is stored or available from environment
 */
export const hasApiKey = (): boolean => {
  // Check environment variable first
  if (import.meta.env.VITE_OPENAI_ADMIN_API_KEY) {
    return true;
  }
  // Then check local storage
  return getApiKey() !== null;
};

/**
 * Get information about saved API Key
 */
export const getApiKeyInfo = (): { createdAt: number; hasKey: boolean } | null => {
  try {
    const stored = localStorage.getItem(OPENAI_ADMIN_API_KEY_STORAGE_KEY);
    if (!stored) return null;

    const data: StoredApiKey = JSON.parse(stored);
    return {
      createdAt: data.createdAt,
      hasKey: !!data.value
    };
  } catch {
    return null;
  }
};

/**
 * Validate API Key format
 */
export const validateApiKeyFormat = (apiKey: string): boolean => {
  if (!apiKey || typeof apiKey !== 'string') return false;

  // Check basic format of OpenAI Admin API Key
  // Only confirm it starts with sk-admin- (actual key length and characters vary)
  const trimmedKey = apiKey.trim();
  return trimmedKey.startsWith('sk-admin-') && trimmedKey.length > 10;
};
